// src/hooks/use-effective-post-layout.ts
import { useMemo } from 'react';
import { useEffectiveWatermarkQuery } from '@/generated/graphql';
import type { EnforcedPostLayout } from '@/lib/layouts/applyEnforcedLayout';

/**
 * Returns the campaign-enforced post layout for a project, or null when there
 * is no active campaign enforcement. When `projectDocumentId` is falsy, the
 * query is skipped and null is returned.
 *
 * The blob comes from `myProject.effective_post_layout` — the same query as the
 * effective watermark (EFFECTIVE_WATERMARK) which was extended to include the
 * field in Part A.
 */
export const useEffectivePostLayout = (
  projectDocumentId: string | null | undefined,
): EnforcedPostLayout | null => {
  const { data } = useEffectiveWatermarkQuery({
    variables: { id: projectDocumentId || '' },
    skip: !projectDocumentId,
    fetchPolicy: 'cache-first',
  });

  return useMemo(() => {
    const blob = data?.myProject?.effective_post_layout;
    if (!blob || typeof blob !== 'object') return null;
    if (!blob.kind) return null;
    return blob as EnforcedPostLayout;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.myProject?.effective_post_layout]);
};

/**
 * Returns the campaign-enforced post layout for a library, or null when there
 * is no active campaign enforcement.
 *
 * Uses `useLibraryEffectiveWatermarkQuery` which was also extended to include
 * `effective_post_layout` in Part A.
 */
import { useLibraryEffectiveWatermarkQuery } from '@/generated/graphql';

export const useLibraryEffectivePostLayout = (
  libraryDocumentId: string | null | undefined,
): EnforcedPostLayout | null => {
  const { data } = useLibraryEffectiveWatermarkQuery({
    variables: { id: libraryDocumentId || '' },
    skip: !libraryDocumentId,
    fetchPolicy: 'cache-first',
  });

  return useMemo(() => {
    const blob = data?.myLibrary?.effective_post_layout;
    if (!blob || typeof blob !== 'object') return null;
    if (!blob.kind) return null;
    return blob as EnforcedPostLayout;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.myLibrary?.effective_post_layout]);
};

/**
 * Campaign-enforced subtitle position (anchor + vertical margin) for a
 * project / library, or null when no campaign sets one. Rides on the same
 * EFFECTIVE_WATERMARK queries as the post layout.
 */
import {
  parseEffectiveSubtitlePosition,
  type EffectiveSubtitlePosition,
} from '@/lib/layouts/enforcedSubtitlePosition';

export const useEffectiveSubtitlePosition = (
  projectDocumentId: string | null | undefined,
): EffectiveSubtitlePosition | null => {
  const { data } = useEffectiveWatermarkQuery({
    variables: { id: projectDocumentId || '' },
    skip: !projectDocumentId,
    fetchPolicy: 'cache-first',
  });

  return useMemo(
    () => parseEffectiveSubtitlePosition(data?.myProject?.effective_subtitle_position),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data?.myProject?.effective_subtitle_position],
  );
};

export const useLibraryEffectiveSubtitlePosition = (
  libraryDocumentId: string | null | undefined,
): EffectiveSubtitlePosition | null => {
  const { data } = useLibraryEffectiveWatermarkQuery({
    variables: { id: libraryDocumentId || '' },
    skip: !libraryDocumentId,
    fetchPolicy: 'cache-first',
  });

  return useMemo(
    () => parseEffectiveSubtitlePosition(data?.myLibrary?.effective_subtitle_position),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data?.myLibrary?.effective_subtitle_position],
  );
};
